import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// GooseBerry-specific accent (only used on the Reflective Scanner / GooseBerry card)
const GREEN = "#61CBA3";
const GREEN_SOFT = "rgba(97,203,163,0.18)";
const GREEN_RING = "rgba(97,203,163,0.30)";

export function HomeView({ onOpenCardGame, onOpenGooseBerry }) {
  return (
    <section className="pt-10 sm:pt-12" data-testid="home-view">
      <div className="mb-8 sm:mb-10">
        <p className="text-[12px] uppercase tracking-[0.18em] text-[#5803FD] font-medium mb-3">SynLab</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-black break-words">
          Choose how you want to explore synthetic users
        </h1>
        <p className="mt-4 text-base sm:text-lg text-black/70 leading-relaxed">
          Choose a tool below to start. <span className="font-medium text-[#61CBA3]">Gooseberry</span> helps you create synthetic users, while the{" "}
          <span className="font-medium text-[#905cff]">Data Literacy Card Game</span> helps you understand how their outputs are shaped.
          Both tools support reflection: they do not replace research with real users.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="home-tool-cards">
        {/* GooseBerry tool, uses the new mascot icon and green accent */}
        <ToolCard
          testid="home-tool-card-gooseberry"
          accent={{
            color: GREEN,
            soft: GREEN_SOFT,
            ring: GREEN_RING,
          }}
          iconNode={
            <img
              src="/brand/synberry-logo.svg"
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="h-20 w-20 rounded-xl object-contain bg-[rgba(97,203,163,0.12)]"
              data-testid="home-gooseberry-icon"
            />
          }
          badge="Reflective scanner"
          title="GooseBerry"
          purpose="Use this on a real design concept."
          description="Upload an image of your design (sketch, wireframe, or mock-up), add some context about your project, and get structured feedback or a synthetic-user dialogue grounded in research metadata."
          steps={[
            "Upload a design",
            "Add project context & method",
            "Review structured feedback",
          ]}
          chips={["Design upload", "Synthetic users", "New perspectives"]}
          cta="Open GooseBerry"
          onClick={onOpenGooseBerry}
        />
        {/* Card Game, keeps the original purple accent unchanged */}
        <ToolCard
          testid="home-tool-card-card-game"
          iconNode={
            <img
              src="/brand/dlg-logo.svg"
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="h-20 w-20 rounded-xl object-contain bg-[rgba(144,92,255,0.10)]"
              data-testid="home-dlg-icon"
            />
          }
          badge="Workshop tool"
          title="Data Literacy Card Game"
          purpose="Use this with clients or team members."
          description="Describe the context, choose two sets of five cards, enter the card codes, and compare the two outputs. Then discuss what the differences reveal."
          steps={[
            "Describe your context",
            "Choose 2 x 5 cards and enter the codes",
            "Compare the outputs from the synthetic users and reflect together",
          ]}
          chips={["Workshop tool", "Bias awareness", "Critical reflection"]}
          cta="Open Card Game"
          onClick={onOpenCardGame}
        />
      </div>

      <div
        className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
        data-testid="home-principles"
        role="complementary"
        aria-label="How we use synthetic users"
      >
        <div className="md:col-span-3 flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-black/55 font-medium">
          <Sparkles className="h-3.5 w-3.5 text-[#AC81FE]" /> How we use synthetic users
        </div>
        {PRINCIPLES.map((p) => (
          <div
            key={p.title}
            className="rounded-2xl border border-black/10 bg-white p-5"
            // explicitly informational, not focusable, not a button
            aria-disabled="true"
          >
            <div className="font-display text-base font-semibold text-black">{p.title}</div>
            <div className="mt-1 text-sm text-black/70 leading-relaxed">{p.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const PRINCIPLES = [
  { title: "Grounding, not guessing", body: "Synthetic users are a starting point, always grounded in named data sources." },
  { title: "Visible bias", body: "Cards and reports make missing perspectives explicit: never hidden under confidence." },
  { title: "Real users next", body: "Every output ends with a suggestion to validate with real users." },
];

function ToolCard({ testid, icon, iconNode, badge, title, purpose, description, steps, chips, cta, onClick, accent }) {
  const c = accent?.color || "#5803FD";
  const cSoft = accent?.soft || "rgba(172,129,254,0.18)";
  const cRing = accent?.ring || "rgba(88,3,253,0.25)";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{ "--accent": c, "--accent-soft": cSoft, "--accent-ring": cRing }}
      className="group h-full text-left rounded-2xl border border-black/10 bg-white p-6 sm:p-7 shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.12)] hover:-translate-y-[2px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--accent-ring)] transition-shadow transition-transform flex flex-col"
    >
      <div className="flex min-h-[80px] items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {iconNode ? (
            <div className="inline-flex h-20 w-20 items-center justify-center">
              {iconNode}
            </div>
          ) : (
            <span
              className="inline-flex h-20 w-20 items-center justify-center rounded-xl"
              style={{ background: cSoft, color: c }}
            >
              {icon}
            </span>
          )}

          <Badge
            variant="outline"
            className="rounded-full bg-white"
            style={{ borderColor: c, color: c }}
          >
            {badge}
          </Badge>
        </div>

        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-black transition-colors group-hover:border-[var(--accent)] group-hover:text-[var(--accent)]">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>

      <div className="mt-5 lg:min-h-[4.5rem] flex items-start">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-black break-words">
          {title}
        </h2>
      </div>

      {purpose && (
        <div
          className="mt-1 lg:min-h-[1rem] text-[12px] uppercase tracking-[0.14em] font-medium"
          style={{ color: c }}
        >
          {purpose}
        </div>
      )}

      <div className="mt-2 lg:min-h-[8rem]">
        <p className="text-sm sm:text-base text-black/70 leading-relaxed">
          {description}
        </p>
      </div>

      {steps && steps.length > 0 && (
        <ol className="mt-4 lg:min-h-[5.75rem] space-y-1.5 text-sm text-black/80">
          {steps.map((s, i) => (
            <li key={s} className="flex items-start gap-2">
              <span
                className="mt-[2px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                style={{ background: cSoft, color: c }}
              >
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {chips.map((cc) => (
          <span
            key={cc}
            className="inline-flex items-center rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-black/70"
          >
            {cc}
          </span>
        ))}
      </div>

      <div className="mt-auto pt-6 flex items-center gap-2 font-medium" style={{ color: c }}>
        {cta}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </button>
  );
}
